import translate from '@/utils/translate';
import type {
  AudienceAgeSelection,
  BaseFilterOption,
  DynamicPlatformFilters,
  Filter,
  MultiSelection,
  MultiSelectionAsync,
  MultiUserInput,
  Range,
  RangeSelection,
  SingleSelection,
  SingleUserInput,
  WeightedFilterOption,
  WeightedMultiSelection,
  WeightedSingleSelection,
} from '@/types/models/creator-crm/filter';
import type {
  CrmFilterType,
  PlatformFilterIds,
} from '@/types/models/creator-crm/filterId';

const activeFilterExtractors: Record<CrmFilterType, ActiveFilterExtractor> = {
  singleSelection: (filter) => {
    const meta = getActiveFilterFromSingleSelectionFilter(
      filter as SingleSelection<BaseFilterOption>
    );
    return meta ? [meta] : [];
  },
  weightedSingleSelection: (filter) => {
    const meta = getActiveFilterFromWeightedSingleSelectionFilter(
      filter as WeightedSingleSelection<WeightedFilterOption>
    );
    return meta ? [meta] : [];
  },
  rangeSelection: (filter) => {
    const meta = getActiveFilterFromRangeSelectionFilter(
      filter as RangeSelection
    );
    return meta ? [meta] : [];
  },
  singleUserInput: (filter) => {
    const meta = getActiveFilterFromSingleUserInputFilter(
      filter as SingleUserInput
    );
    return meta ? [meta] : [];
  },
  multiSelection: (filter) => {
    return getActiveFilterFromMultiSelectionFilter(
      filter as MultiSelection<BaseFilterOption>
    );
  },
  multiSelectionAsync: (filter) => {
    return getActiveFilterFromMultiSelectionAsyncFilter(
      filter as MultiSelectionAsync<BaseFilterOption>
    );
  },
  weightedMultiSelection: (filter) => {
    return getActiveFilterFromWeightedMultiSelectionFilter(
      filter as WeightedMultiSelection<WeightedFilterOption>
    );
  },
  multiUserInput: (filter) => {
    return getActiveFilterFromMultiUserInputFilter(filter as MultiUserInput);
  },
  audienceAge: (filter) => {
    return getActiveFilterFromAudienceAgeFilter(filter as AudienceAgeSelection);
  },
  dynamicPlatformFilters: (filter) => {
    return getActiveFilterFromDynamicPlatformFilters(
      filter as DynamicPlatformFilters
    );
  },
};

export type ActiveFilterMetadata =
  | ActiveSingleSelectionMetadata
  | ActiveWeightedSingleSelectionMetadata
  | ActiveMultiSelectionMetadata
  | ActiveMultiSelectionAsyncMetadata
  | ActiveWeightedMultiSelectionMetadata
  | ActiveRangeSelectionMetadata
  | ActiveSingleUserInputMetadata
  | ActiveMultiUserInputMetadata
  | ActiveAudienceAgeOptionMetadata
  | ActiveAudienceAgeLdaMetadata
  | ActiveDynamicPlatformFiltersMetadata;

type ActiveFilterExtractor = (filter: Filter) => ActiveFilterMetadata[];

function formatWeightedFilterValue(baseValue: string, weight?: number): string {
  return typeof weight === 'number' && weight >= 0
    ? `${baseValue} (${Math.round(weight * 100)}%)`
    : baseValue;
}

export function getActiveFiltersMetadataForFilter(
  filter: Filter
): ActiveFilterMetadata[] {
  if (!filter || filter.isIrrelevant) {
    return [];
  }
  return activeFilterExtractors[filter.type](filter);
}

function getActiveFilterFromSingleSelectionFilter(
  selection: SingleSelection<BaseFilterOption>
): ActiveSingleSelectionMetadata | undefined {
  if (!selection.selected?.value) {
    return undefined;
  }

  const filterName = translate(selection.localeLabelKey);
  const value = translate(
    selection.selected.localeLabelKey,
    selection.selected.translateParams
  );

  return {
    filterName,
    value,
    filterType: 'singleSelection',
    option: selection.selected,
  };
}

function getActiveFilterFromWeightedSingleSelectionFilter(
  selection: WeightedSingleSelection<WeightedFilterOption>
): ActiveWeightedSingleSelectionMetadata | undefined {
  if (!selection.selected?.value) {
    return undefined;
  }

  const filterName = translate(selection.localeLabelKey);
  const value = formatWeightedFilterValue(
    translate(selection.selected.localeLabelKey),
    selection.selected.weight
  );

  return {
    filterName,
    value,
    filterType: 'weightedSingleSelection',
    option: selection.selected,
  };
}

function getActiveFilterFromRangeSelectionFilter(
  selection: RangeSelection
): ActiveRangeSelectionMetadata | undefined {
  const selectedRange = selection.selected;
  if (!selectedRange) {
    return undefined;
  }

  const filterName = translate(selection.localeLabelKey);
  const value = formatRangeValue(selectedRange);
  return {
    filterName,
    value,
    filterType: 'rangeSelection',
    range: selectedRange,
  };
}

function getActiveFilterFromSingleUserInputFilter(
  selection: SingleUserInput
): ActiveSingleUserInputMetadata | undefined {
  if (!selection.input) {
    return undefined;
  }

  const filterName = translate(selection.localeLabelKey);
  return {
    filterName,
    value: selection.input,
    filterType: 'singleUserInput',
    input: selection.input,
  };
}

function getActiveFilterFromMultiSelectionFilter<T extends BaseFilterOption>(
  selection: MultiSelection<T>
): ActiveMultiSelectionMetadata[] {
  const filterName = translate(selection.localeLabelKey);
  return selection.selected.map((opt) => {
    const value = translate(opt.localeLabelKey);
    return {
      filterName,
      value,
      filterType: 'multiSelection',
      option: opt,
    };
  });
}

function getActiveFilterFromMultiSelectionAsyncFilter<
  T extends BaseFilterOption,
>(selection: MultiSelectionAsync<T>): ActiveMultiSelectionAsyncMetadata[] {
  const filterName = translate(selection.localeLabelKey);
  return selection.selected.map((opt) => {
    const value = translate(opt.localeLabelKey);
    return {
      filterName,
      value,
      filterType: 'multiSelectionAsync',
      option: opt,
    };
  });
}

function getActiveFilterFromWeightedMultiSelectionFilter(
  selection: WeightedMultiSelection<WeightedFilterOption>
): ActiveWeightedMultiSelectionMetadata[] {
  const filterName = translate(selection.localeLabelKey);
  return selection.selected.map((opt) => {
    const value = translate(opt.localeLabelKey);
    return {
      filterName,
      value,
      filterType: 'weightedMultiSelection',
      option: opt,
    };
  });
}

function getActiveFilterFromMultiUserInputFilter(
  selection: MultiUserInput
): ActiveMultiUserInputMetadata[] {
  const filterName = translate(selection.localeLabelKey);
  return selection.inputs.map((input) => ({
    filterName,
    value: input,
    filterType: 'multiUserInput',
    input,
  }));
}

function getActiveFilterFromAudienceAgeFilter(
  selection: AudienceAgeSelection
): ActiveFilterMetadata[] {
  const filterName = translate(selection.localeLabelKey);

  const results: ActiveFilterMetadata[] = selection.selected.map((opt) => {
    const value = formatWeightedFilterValue(
      translate(opt.localeLabelKey),
      opt.weight
    );
    return {
      filterName,
      value,
      filterType: 'audienceAge',
      kind: 'option',
      option: opt,
    };
  });

  if (selection.ldaSelected) {
    const value = translate(selection.ldaLocaleLabelKey);
    results.push({
      filterName,
      value,
      filterType: 'audienceAge',
      kind: 'lda',
      isLdaSelected: true,
    });
  }

  return results;
}

function getActiveFilterFromDynamicPlatformFilters(
  selection: DynamicPlatformFilters
): ActiveFilterMetadata[] {
  const platformFilters =
    selection.platformsFilters[selection.platformId] ?? {};

  return Object.entries(platformFilters).flatMap(
    ([platformFilterId, pFilter]) => {
      if (!pFilter) {
        return [];
      }
      return getActiveFiltersMetadataForFilter(pFilter).map((meta) => ({
        ...meta,
        platformFilterId: platformFilterId as PlatformFilterIds,
      }));
    }
  );
}

/**
 * Abbreviates large numbers into a compact string representation.
 *
 * Rules:
 * - \(>= 1,000,000\) -> millions with 1 decimal + `M` (e.g. 1500000 -> "1.5M")
 * - \(>= 1,000\) -> thousands with 0 decimals + `K` (e.g. 5200 -> "5K")
 * - otherwise -> full number (e.g. 500 -> "500")
 */
function abbreviateNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(0)}K`;
  }
  return num.toString();
}

/**
 * Formats a range value without the filter label.
 *
 * Behavior:
 * - If both `min` and `max` are present: `<min> - <max>`
 * - If only `min` is present: `<min>+`
 * - If only `max` is present: `Up to <max>`
 * - If neither is present: empty string
 *
 * Values are abbreviated via `abbreviateNumber` for readability.
 */
function formatRangeValue(range: Range): string {
  const {min, max} = range;
  const hasMin = min != null;
  const hasMax = max != null;

  if (hasMin && hasMax) {
    return `${abbreviateNumber(min)} - ${abbreviateNumber(max)}`;
  }
  if (hasMin) {
    return `${abbreviateNumber(min)}+`;
  }
  if (hasMax) {
    return `Up to ${abbreviateNumber(max)}`;
  }
  return '';
}

type ActiveSingleSelectionMetadata = {
  filterType: 'singleSelection';
  filterName: string;
  value: string;
  option: BaseFilterOption;
  platformFilterId?: PlatformFilterIds;
};

type ActiveWeightedSingleSelectionMetadata = {
  filterType: 'weightedSingleSelection';
  filterName: string;
  value: string;
  option: WeightedFilterOption;
  platformFilterId?: PlatformFilterIds;
};

type ActiveMultiSelectionMetadata = {
  filterType: 'multiSelection';
  filterName: string;
  value: string;
  option: BaseFilterOption;
  platformFilterId?: PlatformFilterIds;
};

type ActiveMultiSelectionAsyncMetadata = {
  filterType: 'multiSelectionAsync';
  filterName: string;
  value: string;
  option: BaseFilterOption;
  platformFilterId?: PlatformFilterIds;
};

type ActiveWeightedMultiSelectionMetadata = {
  filterType: 'weightedMultiSelection';
  filterName: string;
  value: string;
  option: WeightedFilterOption;
  platformFilterId?: PlatformFilterIds;
};

type ActiveRangeSelectionMetadata = {
  filterType: 'rangeSelection';
  filterName: string;
  value: string;
  range: Range;
  platformFilterId?: PlatformFilterIds;
};

type ActiveSingleUserInputMetadata = {
  filterType: 'singleUserInput';
  filterName: string;
  value: string;
  input: string;
  platformFilterId?: PlatformFilterIds;
};

type ActiveMultiUserInputMetadata = {
  filterType: 'multiUserInput';
  filterName: string;
  value: string;
  input: string;
  platformFilterId?: PlatformFilterIds;
};

type ActiveAudienceAgeOptionMetadata = {
  filterType: 'audienceAge';
  kind: 'option';
  filterName: string;
  value: string;
  option: BaseFilterOption;
  platformFilterId?: PlatformFilterIds;
};

type ActiveAudienceAgeLdaMetadata = {
  filterType: 'audienceAge';
  kind: 'lda';
  isLdaSelected: true;
  filterName: string;
  value: string;
  platformFilterId?: PlatformFilterIds;
};

type ActiveDynamicPlatformFiltersMetadata = {
  filterType: 'dynamicPlatformFilters';
  filterName: string;
  value: string;
  platformFilterId?: PlatformFilterIds;
};
